import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { MatchResultsSection } from '../MatchResultsSection';
// Lazy load components to improve initial loading time
const HeadToHeadSection = lazy(() => import('../head-to-head/HeadToHeadSection').then(mod => ({ default: mod.HeadToHeadSection })));
const MatchStatsSection = lazy(() => import('../MatchStatsSection').then(mod => ({ default: mod.MatchStatsSection })));
const ShotsAnalysisSection = lazy(() => import('../ShotsAnalysisSection').then(mod => ({ default: mod.ShotsAnalysisSection })));
const DisciplineSection = lazy(() => import('../DisciplineSection').then(mod => ({ default: mod.DisciplineSection })));
const AttackingStatsSection = lazy(() => import('../AttackingStatsSection').then(mod => ({ default: mod.AttackingStatsSection })));
import { TeamStats } from '../types';
import { LeagueTableControls } from './league-table/LeagueTableControls';
import { LeagueTableDisplay } from './league-table/LeagueTableDisplay';
import { calculateLeagueTableStats } from './league-table/LeagueTableStats';
import { Skeleton } from '@/components/ui/skeleton';
import LoadingState from '@/components/football/LoadingState';
import ErrorDisplay from '@/components/common/ErrorDisplay';
import { toast } from 'sonner';
import { usePointsDeductions } from '@/hooks/usePointsDeductions';

interface StatsDisplayProps {
  teamStats: TeamStats;
  teamName: string;
  matches: any[];
  opponents: string[];
  selectedSeasons: string[];
  league?: string;
  country?: string;
}

export const StatsDisplay: React.FC<StatsDisplayProps> = ({
  teamStats,
  teamName,
  matches,
  opponents,
  selectedSeasons,
  league,
  country
}) => {
  const [viewType, setViewType] = useState<'all' | 'home' | 'away'>('all');
  const [timeType, setTimeType] = useState<'fullTime' | 'halfTime'>('fullTime');
  const [tableError, setTableError] = useState<string | null>(null);
  
  // Get points deductions for the current season
  const { data: pointsDeductions, isLoading: isLoadingDeductions } = usePointsDeductions(
    selectedSeasons.length > 0 ? selectedSeasons[0] : undefined
  );

  // Reset error when dependencies change
  useEffect(() => {
    setTableError(null);
  }, [selectedSeasons, country, league, viewType, timeType]);

  const { 
    data: allSeasonMatches, 
    isLoading, 
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['season-matches', selectedSeasons, country, league],
    queryFn: async () => {
      console.log('Fetching matches for:', { selectedSeasons, country, league });
      if (!country || !league || selectedSeasons.length === 0) {
        console.log('Missing required parameters');
        return [];
      }
      
      try {
        const { data: savedStates, error } = await supabase
          .from('saved_states')
          .select('*')
          .eq('country', country)
          .eq('league', league)
          .in('year', selectedSeasons);
        
        if (error) {
          console.error('Error fetching saved states:', error);
          throw error;
        }
        
        const allMatches: any[] = [];
        savedStates?.forEach(state => {
          if (state.data && Array.isArray(state.data)) {
            console.log(`Adding ${state.data.length} matches from season ${state.year}`);
            allMatches.push(...state.data);
          } else {
            console.warn(`Season ${state.year} has invalid data structure:`, state.data);
          }
        });
        
        console.log('Found matches:', allMatches.length);
        return allMatches;
      } catch (err) {
        console.error('Error fetching match data:', err);
        toast.error('Failed to load league data');
        return [];
      }
    },
    enabled: !!country && !!league && selectedSeasons.length > 0,
    staleTime: 10 * 60 * 1000, // Increase stale time to 10 minutes to reduce refetches
    gcTime: 30 * 60 * 1000, // Changed from cacheTime to gcTime for React Query v5
    retry: 2, // Reduce retry attempts
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Filter points deductions for the current league
  const filteredDeductions = useMemo(() => {
    if (!pointsDeductions || !league) return [];
    
    return pointsDeductions.filter(d => 
      d.league.trim() === league.trim() && 
      selectedSeasons.includes(d.season)
    );
  }, [pointsDeductions, league, selectedSeasons]);

  const teamStandings = useMemo(() => {
    if (!allSeasonMatches || allSeasonMatches.length === 0) {
      console.log('No matches available for league table');
      return [];
    }
    
    try {
      console.log('Calculating team standings with matches:', allSeasonMatches.length);
      // Calculate initial standings without deductions
      const standings = calculateLeagueTableStats({
        matches: allSeasonMatches,
        viewType,
        timeType,
        pointsDeductions: filteredDeductions
      });
      
      // Apply points deductions if available - this is now handled inside calculateLeagueTableStats
      return standings;
    } catch (err) {
      console.error('Error calculating team standings:', err);
      setTableError('Failed to calculate league table');
      return [];
    }
  }, [allSeasonMatches, viewType, timeType, filteredDeductions]);

  // Create a fallback loader for lazy-loaded components
  const LazyLoadFallback = () => <Skeleton className="w-full h-24 rounded-md" />;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <MatchResultsSection 
            teamStats={teamStats} 
            teamName={teamName} 
            matches={matches}
          />
        </div>
        <div className="h-full">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">League Table</h2>
            
            <LeagueTableControls
              viewType={viewType}
              timeType={timeType}
              onViewTypeChange={(type) => {
                setViewType(type);
                setTableError(null);
              }}
              onTimeTypeChange={(type) => {
                setTimeType(type);
                setTableError(null);
              }}
            />

            {isLoading || isLoadingDeductions ? (
              <div className="space-y-4 p-2">
                <LoadingState />
              </div>
            ) : tableError || queryError ? (
              <ErrorDisplay 
                message={tableError || 'Failed to load league table data'} 
                onRetry={refetch}
              />
            ) : teamStandings.length > 0 ? (
              <LeagueTableDisplay
                teamStandings={teamStandings}
                teamName={teamName}
                pointsDeductions={filteredDeductions}
              />
            ) : (
              <div className="flex justify-center items-center h-48 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No league table data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Suspense fallback={<LazyLoadFallback />}>
        <HeadToHeadSection 
          currentTeam={teamName}
          opponents={opponents}
          matches={matches}
          selectedSeasons={selectedSeasons}
        />
      </Suspense>

      <Suspense fallback={<LazyLoadFallback />}>
        <MatchStatsSection 
          teamStats={teamStats} 
          teamName={teamName} 
          matches={matches}
        />
      </Suspense>

      <Suspense fallback={<LazyLoadFallback />}>
        <ShotsAnalysisSection teamStats={teamStats} />
      </Suspense>

      <Suspense fallback={<LazyLoadFallback />}>
        <DisciplineSection 
          teamStats={teamStats} 
          teamName={teamName} 
          matches={matches}
          selectedSeasons={selectedSeasons}
        />
      </Suspense>

      <Suspense fallback={<LazyLoadFallback />}>
        <AttackingStatsSection 
          teamStats={teamStats} 
          teamName={teamName} 
          matches={matches}
        />
      </Suspense>
    </div>
  );
};
